import { A } from '@adornis/base/env-info.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import { ProductSCH } from '@chancenwerk/shared/_cms/products/entities/ProductSCH.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseAbschluss } from '@chancenwerk/shared/courses/CourseAbschluss.js';
import { CourseChancenschule } from '@chancenwerk/shared/courses/CourseChancenschule.js';
import { CourseDigital } from '@chancenwerk/shared/courses/CourseDigital.js';
import { CourseIntensive } from '@chancenwerk/shared/courses/CourseIntensive.js';
import { CourseLernfoerderung } from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { CourseLUS } from '@chancenwerk/shared/courses/CourseLUS.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { ChancenSchule } from '@chancenwerk/shared/master-data-management/partner/entities/ChancenSchule.js';
import { School } from '@chancenwerk/shared/master-data-management/partner/entities/School.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { ChancenRoleAspect } from '@chancenwerk/shared/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { of, switchMap } from 'rxjs';
import { BoardRoutes } from '../routes.js';
import './board-component-abs-overview-grid.js';
import './board-component-flat-loader.js';
import './board-component-lkk-overview-grid.js';
import './board-component-sch-overview-grid.js';

const courseSelector: BaseQLSelectionSet<Course | CourseAbschluss | CourseLernfoerderung> = {
  _id: 1,
  _class: 1,
  name: 1,
  kpfID: 1,
  location: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  leKoIDs: 1,
  osStudentIDs: 1,
  usStudentIDs: 1,
  diKuIDs: 1,
  schuKoIDs: 1,
  schuKos: { _id: 1, _class: 1 },
  startDate: 1,
  plannedStartDate: 1,
  subject: 1,
  type: 1,
  maxAttendanceCount: 1,
  version: 1,
};

/**
 * @customElement board-component-overview-card
 */
@customElement('board-component-overview-card')
export class BoardComponentOverviewCard extends ChemistryLitElement {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) public kpf = new RXController(this, new KPF({}));
  @property({ attribute: false }) public showProductLabel = true;

  @state() private readonly _coursesFromKPF = new RXController(
    this,
    this.kpf.observable.pipe(
      switchMap(kpf => {
        if (kpf.produkt?._class !== ProductLKK._class) {
          return of([]);
        }

        return Course.subscribeCoursesByKPFID([kpf._id])(courseSelector);
      }),
    ),
  );

  @state() private readonly _intensiveCoursesFromKPF = new RXController(
    this,
    this.kpf.observable.pipe(
      switchMap(kpf => {
        if (kpf.produkt?._class !== ProductLKK._class) {
          return of([]);
        }

        return Course.subscribeCoursesByKPFID([kpf._id], 'CourseIntensive')(courseSelector);
      }),
    ),
  );

  @state() private readonly _chancenschuleCoursesFromKPF = new RXController(
    this,
    this.kpf.observable.pipe(
      switchMap(kpf => {
        if (kpf.produkt?._class !== ChancenSchule._class) {
          return of([]);
        }
        return Course.subscribeCoursesByKPFID([kpf._id], CourseChancenschule._class)(courseSelector);
      }),
    ),
  );

  @state() private readonly _lusCoursesFromKPF = new RXController(
    this,
    this.kpf.observable.pipe(
      switchMap(kpf => {
        if (kpf.produkt?._class !== 'ProductLUS') {
          return of([]);
        }
        return Course.subscribeCoursesByKPFID([kpf._id], CourseLUS._class)(courseSelector);
      }),
    ),
  );

  @state() private readonly _chancenabschlussCoursesFromKPF = new RXController(
    this,
    this.kpf.observable.pipe(
      switchMap(kpf => {
        if (kpf.produkt?._class !== 'ProductABS') {
          return of([]);
        }
        return Course.subscribeCoursesByKPFID([kpf._id], CourseAbschluss._class)(courseSelector);
      }),
    ),
  );

  @state() private readonly _dikus = new RXController(
    this,
    this.kpf.observable.pipe(
      switchMap(async kpf => {
        if (kpf.produkt?._class !== 'ProductABS') {
          return 0;
        }
        return UserAPI.getCountByFilter(
          new ChancenwerkUserFilter({
            aspects: [
              new BooleanAspect({ customID: 'active', value: true }),
              new ChancenRoleAspect({ value: Role.DKL }),
            ],
          }),
        )();
      }),
    ),
  );

  @state() private readonly _digitalCoursesFromKPF = new RXController(
    this,
    this.kpf.observable.pipe(switchMap(kpf => CourseDigital.subscribeDigitialCourseByKPFID(kpf._id)(courseSelector))),
  );

  private _isLoading() {
    return (
      this.kpf.isLoading ||
      this._coursesFromKPF.isLoading ||
      this._intensiveCoursesFromKPF.isLoading ||
      this._digitalCoursesFromKPF.isLoading
    );
  }

  private _stats() {
    const produkt = this.kpf.value.produkt;
    if (produkt?._class === 'ProductABS') {
      return html` <cr-flex horizontal space="sm">
          <cr-text bold>DiKu</cr-text>
          <cr-text> ${this._dikus.value} </cr-text>
        </cr-flex>

        <cr-flex horizontal space="sm">
          <cr-text bold>Jugendliche</cr-text>
          <cr-text> ${this.kpf.value.osStudents?.length ?? 0} </cr-text>
        </cr-flex>`;
    }

    return html` <cr-flex horizontal space="sm">
        <cr-text bold>SchuKos</cr-text>
        <cr-text> ${this.kpf.value.schuKos?.length ?? 0} </cr-text>
      </cr-flex>

      <cr-flex horizontal crossaxis-center space="sm">
        <cr-text bold>LeKos</cr-text>
        <cr-text> ${this.kpf.value.leKos?.length ?? 0} </cr-text>
      </cr-flex>

      <cr-flex horizontal space="sm">
        <cr-text bold>Jugendliche</cr-text>
        <cr-text> ${this.kpf.value.osStudents?.length ?? 0} </cr-text>
      </cr-flex>

      <cr-flex horizontal space="sm">
        <cr-text bold>Kinder</cr-text>
        <cr-text> ${this.kpf.value.usStudents?.length ?? 0} </cr-text>
      </cr-flex>`;
  }

  override render() {
    const breakpoint = this._size.tablet || this._size.mobile || (this._size.width ?? 1400) < 1400;
    const produkt = this.kpf.value.produkt;
    if (!produkt) return 'kein Produkt';
    return html` <cr-card
      ${acss({ width: '100%', background: 'white', overflow: 'auto' })}
      wrap
      padding="md"
      space="md"
    >
      <cr-flex
        horizontal
        crossaxis-center
        space="md"
        ?space-between=${!breakpoint}
        ${css({ display: breakpoint ? 'block' : 'flex' })}
      >
        <cr-flex start crossaxis-center horizontal space="md">
          ${this.showProductLabel ? html` <cr-text bold> ${produkt.name} </cr-text> ` : nothing}
          <cr-text bold> ${this.kpf.value.kuerzel} </cr-text>
          <cr-text bold> ${this.kpf.value.school?.name} </cr-text>
          <cr-text bold>
            ${this.kpf.value.school instanceof School ? this.kpf.value.school.address?.zipCity?.city : ''}
          </cr-text>
        </cr-flex>
        <cr-flex
          crossaxis-center
          horizontal
          space="lg"
          padding="xs"
          ${css({
            border: '1px solid',
            borderRadius: this.sizes.borderRadiusSecondary,
            marginTop: breakpoint ? this.spacing.md : '',
          })}
        >
          ${!this._isLoading()
            ? this._stats()
            : html` <cr-loader ${css({ height: '12px', width: '12px' })}></cr-loader>`}
        </cr-flex>
      </cr-flex>
      ${choose(
        produkt._class,
        [
          [
            ProductLKK._class,
            () => {
              return html` ${this._getLernförderungCourseOverview()} ${this._getOtherCourseOverview()}
              ${this._getManagement()}`;
            },
          ],
          [
            ProductSCH._class,
            () => {
              return html` ${this._getSchCourseOverview()}`;
            },
          ],
          [
            'ProductLUS',
            () => {
              return html` ${this._getLusCourseOverview()}`;
            },
          ],
          ...(this._devProducts() as any),
        ],
        () => html` <cr-text> Das Produkt ${produkt.name} ist noch nicht implementiert! </cr-text>`,
      )}
    </cr-card>`;
  }

  private _devProducts() {
    if (!A.isDevelopment && baseConfig.get('DEPLOYMENT') !== 'staging') {
      return [];
    }

    return [
      [
        'ProductABS',
        () => {
          return html` ${this._getAbsCourseOverview()}`;
        },
      ],
    ];
  }

  private _getManagement() {
    return permissionGuard(
      ChancenPermission.board.MANAGE_KPF_BOARD,
      () => {
        return html`<x-toggle-content label="Vertretung planen">
          <cr-button
            icon="shield_person"
            href=${`${BoardRoutes.kpfManagement}/${this.kpf.value._id}`}
            ${css({ maxWidth: '300px' })}
          >
            Öffnen
          </cr-button>
        </x-toggle-content>`;
      },
      { forbiddenTemplate: nothing, context: ANY_CONTEXT },
    );
  }

  private _getOtherCourseOverview() {
    return html` ${this._getIntensivCourseOverview()} ${this._getDigitialIntensivCourseOverview()}`;
  }

  private _getIntensivCourseOverview() {
    if (!this._intensiveCoursesFromKPF.value || this._intensiveCoursesFromKPF.value?.length === 0) {
      return html`<x-toggle-content label="vor-Ort Intensivkurse">
        <cr-flex horizontal space-between space="sm">
          <cr-text>Noch keine vor-Ort Intensivkurse vorhanden</cr-text>
          <cr-button
            mode="fab"
            icon="chevron_right"
            href="${`${BoardRoutes.details}/${CourseIntensive._class}/${this.kpf.value._id}`}"
          >
            <cr-tooltip> <cr-text> Details </cr-text> </cr-tooltip>
          </cr-button>
        </cr-flex>
      </x-toggle-content>`;
    }

    return html` <board-component-lkk-overview-grid
      name="vor-Ort Intensivkurse"
      .class=${CourseIntensive._class}
      .loading=${this._isLoading()}
      .kpf=${this.kpf.value}
      .courses=${this._intensiveCoursesFromKPF.value}
      .hide=${['Kinder']}
    >
    </board-component-lkk-overview-grid>`;
  }

  private _getDigitialIntensivCourseOverview() {
    if (!this._digitalCoursesFromKPF.value || this._digitalCoursesFromKPF.value?.length === 0) {
      return html`<x-toggle-content label="digitale Intensivkurse">
        <cr-flex horizontal space-between space="sm">
          <cr-text>Noch keine digitalen Intensivkurse hinzugefügt</cr-text>
          <cr-button
            mode="fab"
            icon="chevron_right"
            href=${`${BoardRoutes.details}/${CourseDigital._class}KPF/${this.kpf.value._id}`}
          >
            <cr-tooltip> <cr-text> Details </cr-text> </cr-tooltip>
          </cr-button>
        </cr-flex>
      </x-toggle-content>`;
    }

    return html` <board-component-lkk-overview-grid
      name="digitale Intensivkurse"
      .class=${CourseDigital._class + 'KPF'}
      .loading=${this._isLoading()}
      .kpf=${this.kpf.value}
      .courses=${this._digitalCoursesFromKPF.value}
      .hide=${['Kinder', 'ShuKo']}
    >
    </board-component-lkk-overview-grid>`;
  }

  private _getLernförderungCourseOverview() {
    return html`
      <board-component-lkk-overview-grid
        name="Lernförderung"
        .class=${CourseLernfoerderung._class}
        .loading=${this._isLoading()}
        .kpf=${this.kpf.value}
        .courses=${this._coursesFromKPF.value ?? []}
      >
      </board-component-lkk-overview-grid>
    `;
  }

  private _getLusCourseOverview() {
    return html`
      <board-component-sch-overview-grid
        name="Leseadler und Schreibfüchse"
        .class=${CourseLUS._class}
        .loading=${this._isLoading()}
        .kpf=${this.kpf.value}
        .courses=${this._lusCoursesFromKPF.value ?? []}
        .hide=${['SchuKo', 'Jugendlich']}
      >
      </board-component-sch-overview-grid>
    `;
  }

  private _getSchCourseOverview() {
    return html`
      <board-component-sch-overview-grid
        name="Chancenschule"
        .class=${CourseChancenschule._class}
        .loading=${this._isLoading()}
        .kpf=${this.kpf.value}
        .courses=${this._chancenschuleCoursesFromKPF.value ?? []}
        .hide=${['SchuKo', 'Jugendlich']}
      >
      </board-component-sch-overview-grid>
    `;
  }

  private _getAbsCourseOverview() {
    return html`
      <board-component-abs-overview-grid
        name="CHANCENabschluss"
        .class=${CourseAbschluss._class}
        .loading=${this._isLoading()}
        .kpf=${this.kpf.value}
        .courses=${this._chancenabschlussCoursesFromKPF.value ?? []}
        .hide=${['SchuKo', 'Jugendlich']}
      >
      </board-component-abs-overview-grid>
    `;
  }
}
